import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { RoomSplit } from '../../../types';
import { DrillTile } from './DrillTile';
import { DrillBarChart } from './charts/DrillBarChart';
import '../../../styles/drill-capacity.css';

function roomLabel(r: RoomSplit['rows'][number]): string {
  return r.outletName ? `${r.outletName} · ${r.name}` : r.name;
}

/**
 * Occupancy per room: each room's occupied seat hours over its share of the
 * venue's seat hours on offer. Hidden when the scope has fewer than two rooms
 * in use (the server sends no split).
 */
export function DrillRoomSection({ split }: { metricKey: string; split?: RoomSplit | null }) {
  const rows = split?.rows ?? [];

  const inspector = useMemo(
    () => (
      <div className="dcrux-rank-list">
        {rows.map((r, i) => (
          <React.Fragment key={r.id}>
            <div className="dcrux-rank-row dcrux-rank-row-adv">
              <span className="dcrux-outlet-rank">{i + 1}</span>
              <span className="dcrux-outlet-name" title={roomLabel(r)}>
                {roomLabel(r)}
              </span>
              <span className="dcrux-si-track">
                <span
                  className="dcrux-si-fill"
                  style={{ width: `${Math.max(2, Math.min(100, r.pct))}%` }}
                />
              </span>
              <span className="dcrux-outlet-val">{r.pct}%</span>
            </div>
            <div className="dcrux-rank-sub">
              {i18n.t('performance:drillUi.byRoomSeatHours', {
                used: fmt.num(r.occupiedSeatHours),
                offered: fmt.num(r.availableSeatHours),
                seats: fmt.num(r.seats),
              })}
              {' · '}
              {i18n.t('performance:drillUi.nTotal', { n: fmt.num(r.reservations) })}
            </div>
          </React.Fragment>
        ))}
        {split?.moreCount ? (
          <div className="dcrux-rank-sub">
            {i18n.t('performance:drillUi.byRoomMore', { count: split.moreCount })}
          </div>
        ) : null}
      </div>
    ),
    [rows, split?.moreCount],
  );

  if (!split || rows.length < 2) return null;

  const top = rows.slice(0, 4);
  const chartData = top.map(r => ({
    name: r.name.length > 22 ? `${r.name.slice(0, 21)}…` : r.name,
    fullName: roomLabel(r),
    value: r.pct,
  }));

  return (
    <DrillTile
      sectionId="byRoom"
      span={6}
      title={i18n.t('performance:drillUi.byRoom')}
      meta={i18n.t('performance:drillUi.byRoomMeta', {
        n: fmt.num(split.n),
        coverage: split.coveragePct,
      })}
      inspector={inspector}
    >
      <DrillBarChart
        data={chartData}
        fmtVal={v => `${Math.round(v)}%`}
        height={Math.max(120, 36 + chartData.length * 28)}
        horizontal
        showValues
      />
    </DrillTile>
  );
}
